/**
 * **C03 · The closeout report per area** (Stream C, Documents.dc.html): "How did it go?", one page
 * per area, laid out by the `closeout_page` engine from Money's area closeout (budget, receipts,
 * hours, what was used, the chair's closeout). Scored against the drawing by walk 99g.
 */
import type { ReactNode } from 'react';
import { closeoutPage } from '@cider/domain/engines';
import { formatCalendarDate, formatDate } from '@cider/ui';
import { useLoad, type Report } from '../views/money/shared.js';
import { usePrinted } from './printed.js';
import { DrawnPages } from './drawn-doc.js';

export function AreaCloseout({ fy, areaId }: { fy: number; areaId: string }): ReactNode {
  const report = useLoad<Report>(
    `/money/reports/area-closeout?fy=${String(fy)}&areaId=${encodeURIComponent(areaId)}`,
  );
  const ready = report.data !== null || report.error !== null;
  usePrinted('doc-area-closeout', ready, { ref: areaId, year: fy - 1 });
  if (!ready) return <p>Reading the figures…</p>;
  const page = closeoutPage({
    fiscalYear: fy,
    // A report with no figures yet still names its area; its sections are empty, so NO DATA.
    report: report.data ?? null,
    // The day Money's report was read, on the festival's clock: `Sep 20, 2026`.
    prepared: /^\d{4}-\d{2}-\d{2}$/.test(report.data?.asOf ?? '')
      ? formatCalendarDate(report.data?.asOf ?? '')
      : formatDate(new Date(), { year: true }),
  });
  return <DrawnPages pages={[page]} />;
}
